import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx } from '../internal/cx';

/**
 * Nós que ESTE componente abriu via `showModal()` e ainda não fechou —
 * substituto de `node.matches(':modal')` para ambientes onde o seletor não é
 * suportado (ver `isOpenedModally` abaixo). `WeakSet` para não vazar memória:
 * um `<dialog>` desmontado sai sozinho quando não há mais referência a ele.
 */
const modallyOpenedNodes = new WeakSet<HTMLDialogElement>();

/** `null` = ainda não detectado nesta sessão do módulo; `true`/`false` = resultado
 * memorizado (a capacidade não muda em runtime, então uma exceção basta). */
let modalSelectorSupported: boolean | null = null;

/**
 * `showModal()`/`close()` também não existem no jsdom — o elemento só reflete
 * o atributo `open` (`HTMLDialogElement-impl.js` do jsdom estende
 * `HTMLElement` puro, sem a API imperativa nem o top layer). Checagem de
 * capacidade separada da de `:modal`: sem estes dois métodos não há o que
 * chamar, e o atributo `open` declarativo do JSX já basta para refletir o
 * estado (progressive enhancement, ver o comentário no JSX abaixo) — o que
 * falta é só a promoção a top layer real, que não existe para simular fora de
 * um navegador de verdade.
 */
function supportsNativeDialog(node: HTMLDialogElement): boolean {
  return typeof node.showModal === 'function' && typeof node.close === 'function';
}

/**
 * Distingue "o `<dialog>` está de fato em modo modal/top-layer" de "o atributo
 * `open` está presente" sem depender de um seletor CSS que nem todo motor
 * implementa.
 *
 * **Por que não usar só `node.matches(':modal')`:** é o jeito correto e é o
 * que um Chromium real responde de verdade (preservado abaixo). Mas o motor
 * de seletor do jsdom (nwsapi) não reconhece `:modal` e LANÇA `SyntaxError:
 * unknown pseudo-class selector ':modal'` em vez de simplesmente não casar —
 * isso derrubava toda a suíte de testes que exercita o command palette em
 * jsdom (ver `apps/web/test/rtl/TenantShell.test.tsx`), sem que o produto real
 * tivesse regredido em nada.
 *
 * Detecção de capacidade, uma vez por processo: se `:modal` for suportado
 * (qualquer navegador real, incluindo Chromium do Playwright), a fonte de
 * verdade continua sendo o navegador, sem exceção. Só quando o seletor não
 * compila (jsdom) o fallback localizado assume — rastreando, neste módulo,
 * quais nós ESTE componente abriu modalmente via `showModal()` e ainda não
 * fechou. Nenhuma bifurcação por `NODE_ENV`: a checagem é por capacidade real
 * do ambiente, não por variável de configuração.
 */
function isOpenedModally(node: HTMLDialogElement): boolean {
  if (modalSelectorSupported !== false) {
    try {
      const result = node.matches(':modal');
      modalSelectorSupported = true;
      return result;
    } catch {
      modalSelectorSupported = false;
    }
  }
  return modallyOpenedNodes.has(node);
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
}

/**
 * Wrapper fino sobre `<dialog>` nativo: foco preso, fechamento por Esc/backdrop e
 * empilhamento em top layer vêm de graça do navegador via `showModal()`/`close()` —
 * evita reimplementar focus-trap manualmente (fonte comum de bug de teclado). O
 * atributo `open` também é declarado de forma declarativa (progressive
 * enhancement / testável sem efeito rodando, ex.: `renderToStaticMarkup`).
 */
export function Dialog({ open, onClose, title, description, children, className }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = description ? `${titleId}-description` : undefined;
  /** Ver comentário do `useLayoutEffect` abaixo — silencia o `close` nativo
   * disparado pela correção interna (fechar o `<dialog>` não-modal antes de
   * reabri-lo via `showModal()`), que senão chamaria `onClose` do consumidor
   * e fechava de volta o que acabou de abrir (achado real, rodando o command
   * palette num Chromium de verdade: o diálogo abria e fechava no mesmo
   * frame). */
  const suppressNextCloseRef = useRef(false);

  /**
   * `useLayoutEffect`, e não `useEffect` — e uma checagem por `:modal`, não só
   * por `node.open`.
   *
   * **Achado real (R-UX02-P01/D-101, rodando o command palette com a gaveta
   * mobile aberta ao mesmo tempo):** o atributo `open` declarativo do JSX (a
   * linha abaixo, `open={open || undefined}`, existe para "progressive
   * enhancement" — o `<dialog>` continua visível sem JS/SSR) e a chamada
   * imperativa `showModal()` são DUAS formas diferentes de abrir um
   * `<dialog>`, e o navegador aplica o ATRIBUTO antes de qualquer efeito
   * rodar (React comita o DOM antes de disparar efeitos). Resultado: quando
   * `open` vira `true`, `node.open` **já é `true`** por causa do atributo — a
   * checagem antiga (`open && !node.open`) nunca chamava `showModal()`, e o
   * diálogo abria NÃO-MODAL (fluxo normal do documento, fora do top layer).
   * Um `<dialog>` não-modal fica sujeito à cascata de z-index normal — contra
   * um scrim/gaveta com z-index alto por cima, ele perde, e cliques nele são
   * interceptados pelo que está por cima (exatamente o erro do Playwright:
   * "... intercepts pointer events"). Também explica por que Esc não fechava
   * sozinho: o evento nativo `cancel` só existe para diálogo aberto via
   * `showModal()`.
   *
   * `isOpenedModally` (acima) distingue os dois — via `:modal` quando o
   * ambiente suporta o seletor (todo navegador real), com fallback de
   * capacidade para jsdom (ver o comentário da função). Se `open` é `true`
   * mas o elemento não está modal (abriu pelo atributo), fecha primeiro — só
   * depois chamar `showModal()` num `<dialog>` já com o atributo `open` lança
   * `InvalidStateError`. `useLayoutEffect` (não `useEffect`) evita o flash
   * visual do estado não-modal antes da correção: roda antes do navegador
   * pintar o frame.
   *
   * **`suppressNextCloseRef`**: `node.close()` dispara o evento nativo
   * `close` SINCRONAMENTE — e o outro efeito abaixo já escuta `close` para
   * chamar `onClose()` do consumidor. Sem a supressão, a correção acima
   * ("fecha o não-modal antes de reabrir modal") disparava `onClose()`,
   * que no `GoToPalette` chama `setOpen(false)` — fechando de volta o
   * diálogo que `showModal()` tinha acabado de abrir, no MESMO ciclo. A
   * flag marca "este `close()` é interno, não conte para o consumidor".
   */
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!supportsNativeDialog(node)) {
      // Sem `showModal()`/`close()` (jsdom) não há top layer para promover: o
      // atributo `open` declarativo (JSX abaixo) já é a única fonte de verdade
      // possível, e o resto deste efeito não tem o que fazer.
      return;
    }
    if (open) {
      if (node.open && !isOpenedModally(node)) {
        suppressNextCloseRef.current = true;
        node.close();
        modallyOpenedNodes.delete(node);
      }
      if (!node.open) {
        node.showModal();
        modallyOpenedNodes.add(node);
      }
    } else if (node.open || isOpenedModally(node)) {
      // O React já removeu o atributo `open` na fase de mutação (antes deste efeito).
      // Sem o `close()` nativo o Chromium mantém o diálogo modal no top layer e o resto
      // do documento inerte — foco e Tab mortos até recarregar (medido no L4-S da
      // R-UX02-P02, Chrome 151). Reflete o atributo de volta só para o `close()` executar
      // os passos de fechamento (sair do top layer, evento `close`, restore de foco).
      if (!node.open) node.open = true;
      node.close();
      modallyOpenedNodes.delete(node);
    }
  }, [open]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    function handleCancel(event: Event) {
      // Esc dispara 'cancel' antes de 'close' — intercepta para o consumidor
      // continuar dono do estado `open` (padrão controlado, igual a um <input>).
      event.preventDefault();
      onClose();
    }
    function handleClose() {
      // O `<dialog>` acabou de fechar por qualquer via (nossa ou do navegador)
      // — mantém o fallback de `:modal` (acima) consistente com a realidade.
      // (`node` já foi checado não-nulo acima; a checagem aqui é só para o
      // TypeScript, que não propaga a narrowing para dentro de function
      // declarations aninhadas.)
      if (node) modallyOpenedNodes.delete(node);
      if (suppressNextCloseRef.current) {
        suppressNextCloseRef.current = false;
        return;
      }
      onClose();
    }

    node.addEventListener('cancel', handleCancel);
    node.addEventListener('close', handleClose);
    return () => {
      node.removeEventListener('cancel', handleCancel);
      node.removeEventListener('close', handleClose);
    };
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      open={open || undefined}
      className={cx('argink-dialog', className)}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClick={(event) => {
        // clique fora do card de conteúdo (no próprio elemento <dialog>, que ocupa
        // a área do backdrop nativo) fecha — igual ao comportamento esperado de
        // "clicar fora fecha".
        if (event.target === ref.current) {
          onClose();
        }
      }}
    >
      <div className="argink-dialog__content">
        <div className="argink-dialog__header">
          <h2 className="argink-dialog__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="argink-dialog__close argink-focusable"
            aria-label="Fechar"
            onClick={onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        {description ? (
          <p className="argink-dialog__description" id={descriptionId}>
            {description}
          </p>
        ) : null}
        <div className="argink-dialog__body">{children}</div>
      </div>
    </dialog>
  );
}
